// NOW, inside the ORB. There is no separate NOW screen: the orb that hangs
// from the compass IS the moment. Three orbs share one frame and one set of
// five ORDER rows (docs/redesign/ORB_SPEC.md):
//   STEP  the current task        (opens here)
//   TODAY the day
//   AIM   the week                (Goal / Endeavour reuse the Aim layout)
// Each row = a question, a one-line summary (collapsed), a compact widget (open)
// and a swipe-right target (the full tool). The row definitions live in ./orb/.
// The O·R·D·E·R letters sit in a thin crescent on the orb's left edge; ◐ / ☰
// switches between one letter filling the orb (Focus) and five rows (Rows).
// NUMA is not one of the rows.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Animated, Image, PanResponder, Pressable, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, ClipPath, Defs, G, Image as SvgImage, Line, Path, Rect, Stop } from 'react-native-svg';
import type { GearId } from '../olmps/ascension';
import type { Sector } from '../domain/olmps';
import type { UseTodayData } from '../olmps/useTodayData';
import type { OracleSpine } from '../olmps/oracle-spine-data';
import type { OracleProject } from '../olmps/useOracleProjects';
import { parseTkneLink } from '../olmps/links/tkneThemis';
import { setKrnosView } from '../olmps/links/krnosView';
import { supabase } from '../lib/supabase';
import type { NowStep } from './useCurrentStep';
import { GEAR_LABEL, GEAR_ORDER } from './roster';
import { DISPLAY_FONT, SECTOR_NAME, makeStyles, useV10 } from './theme';
import { tourBus, useTourTarget } from './onboarding';
import { OrbScale } from './orb/ui';
import { useOrbActions } from './orb/actions';
import { useOrbData, useOrbNotes } from './orb/data';
import { useRunner, mmss } from './orb/runner';
import { buildPool, defaultStep } from './orb/pool';
import { aimsOf, goalName } from './orb/shared';
import { aimRows } from './orb/aimRows';
import { todayObjective, todayRows } from './orb/todayRows';
import { emptyStepRows, stepRows } from './orb/stepRows';
import { dateKeyOf } from './orb/time';
import type { OrbItem, OrbLevel, ProtocolLite, RoutineStep, RowCtx, RowSet, SwipeAction, ToolOpts } from './orb/types';
import { useOrbWidgets } from './orb/widgets/useOrbWidgets';
import { SectionDeck, usePins } from './orb/widgets/Deck';
import { OrientFields, ORIENT_Q } from './orb/OrientFields';
import { atlasOf, currentOf, orientKey, orientOf, type OrientSentences } from '../olmps/atlas-data';
import { orientPrefill, resolvePath } from '../olmps/atlas-tree';
import { SentenceField } from './orb/SentenceField';
import { itemsForSector } from './orb/data';
import { atlasSentenceOf, saveAtlasSentence } from '../olmps/atlas-data';
import { ReviseOutcome, REVISE_NOTE_Q, REVISE_OUTCOMES, type ReviseOutcomeId } from './orb/ReviseOutcome';
import { ScreenHelp } from './ScreenHelp';
import { orbHelp } from '../olmps/help/orbHelp';

const DIRECT_Q = { do: 'What should I do?', order: 'In what order?' } as const;
const REGULATE_Q = { need: 'What resources do I need?', have: 'Do I have everything I need?' } as const;
const EXECUTE_Q = { now: 'What am I doing right now?', how: 'How do I carry it out?' } as const;
import { cachePoros, cachedPoros, emptyPoros, needsSentence, porosKey, porosOf, readiness, readinessSentence, savePoros, setPorosTarget, subscribePoros } from '../olmps/poros-data';
import type { WidgetCtx } from './orb/widgets/types';

/** RN 0.81 honours pointerEvents in style, but ImageStyle typings omit it. */
const NO_TOUCH = { pointerEvents: 'none' } as object;

/** Which ORDER row is open for a given time of day. Morning, before the
 *  evening session: O → R → D in turn; daytime: E; evening: the last R. */
export function timeGear(now: Date): GearId {
  const h = now.getHours() + now.getMinutes() / 60;
  if (h >= 5 && h < 7) return 'orient';
  if (h >= 7 && h < 8) return 'regulate';
  if (h >= 8 && h < 10) return 'direct';
  if (h >= 10 && h < 18) return 'execute';
  return 'revise';
}

/** The THEMIS routine for a task: a protocol made from its TKNE step, else one titled like it. */
function matchProtocol(protocols: ProtocolLite[], step: OrbItem): ProtocolLite | null {
  const title = step.title.trim().toLowerCase();
  if (!title) return null;
  const linked = protocols.find((p) => {
    const l = parseTkneLink(p.notes);
    return !!l && ((!!step.stageKey && l.stageKey === step.stageKey) || (!!l.step && l.step.trim().toLowerCase() === title));
  });
  if (linked) return linked;
  const same = protocols.find((p) => p.title.trim().toLowerCase() === title);
  if (same) return same;
  return protocols.find((p) => { const a = p.title.trim().toLowerCase(); return a.length >= 4 && title.length >= 4 && (a.includes(title) || title.includes(a)); }) ?? null;
}

export interface NowOrbProps {
  size: number;
  userId: string | undefined;
  today: UseTodayData;
  /** Selected compass sector (null until one is picked). */
  sector: Sector | null;
  /** Which orb: S step, T today, A aim (G / E use the Aim layout). */
  level: OrbLevel;
  onLevel: (l: OrbLevel) => void;
  steps: NowStep[];
  projects: Record<Sector, OracleProject[]>;
  spine: OracleSpine | null;
  now: Date;
  /** The selected ORDER letter (time of day by default). */
  selectedGear: GearId;
  onPickRow: (gear: GearId) => void;
  /** Open a tool on the tool screen. */
  onOpenTool: (toolId: string, opts?: ToolOpts) => void;
  /** Open the morning or evening session. */
  onOpenSession?: (kind: 'morning' | 'night') => void;
  /** Re-read the current step / today after a write. */
  onRefresh: () => void;
  /** The selected row's tool, so ▶ and the canvas swipe land on it. */
  onTarget: (t: { tool: string; opts?: ToolOpts }) => void;
  /** The orb fills this with "do the selected row's swipe-right target". */
  swipeRef: React.MutableRefObject<(() => void) | null>;
  /** Open the deity chat (DeitySheet) for a widget's seat. */
  onAgent?: (seatId: string) => void;
}

// Orb art cut from Joel's orb mockup (scripts/build_orb_art.py): the white marble
// of the selected crescent slice and the glowing
// gold rim (its ring centre sits at ORB_RIM_FRAC of the sprite's half-width).
// The dark marble body is not drawn: the orb has no background, so the sky shows
// through and the theme's text colours read in both day and night.
const ORB_MARBLE_LIGHT = require('../../assets/v10/orb/orb_marble_light.jpg');
const ORB_RIM = require('../../assets/v10/orb/orb_rim.png');
const ORB_RIM_FRAC = 0.8257;
/** Clear space (px) between the rim's inner edge and a crescent letter. */
const RIM_GAP = 9;

/** Largest font (≤ max, ≥ MIN_FS) at which a whole answer fits a w×h band. Below
 *  that it stays at MIN_FS and the band's last line ellipsizes — it never drops a
 *  line silently. Char width is estimated (display serif ≈ 0.58em, generous so the last line never clips). */
const MIN_FS = 9;
function fitAnswer(answer: string[], w: number, h: number, max: number): { fs: number; lines: number } {
  const len = answer.join('  ·  ').length * 1.12;
  for (let fs = max; fs >= MIN_FS; fs -= 0.5) {
    const lines = Math.floor(h / (fs * 1.2));
    if (lines >= 1 && Math.ceil(len / Math.max(1, Math.floor(w / (fs * 0.58)))) <= lines) return { fs, lines };
  }
  return { fs: MIN_FS, lines: Math.max(1, Math.floor(h / (MIN_FS * 1.2))) };
}

export function NowOrb(p: NowOrbProps) {
  const t = useV10();
  const styles = useStyles();
  const orbRow = useTourTarget('orb.row');
  const { size: S } = p;
  const k = S / 300;
  // The orb ring is a 2px border: children live in the (S-4) padding box, so the
  // lune's outer edge is that box's inscribed circle — the ring's inner edge.
  const R = S / 2 - 2;
  const g = p.selectedGear;
  const level = p.level;
  const sector = p.sector;
  const sectorName = sector ? SECTOR_NAME[sector] : '';
  const todayKey = dateKeyOf(p.now);

  const data = useOrbData(p.userId, p.today, p.now);
  const notes = useOrbNotes();

  // ---- drill-down: Aim E → that day's Today orb; Today E → that task's Step orb.
  const [dayFocus, setDayFocus] = useState<string | null>(null);
  const [stepDay, setStepDay] = useState<string | null>(null);
  const [stepSel, setStepSel] = useState<string | null>(null);
  const selfLevel = useRef(false);
  useEffect(() => {
    // A level change that did not come from a drill (compass pick, label tap) starts fresh.
    if (selfLevel.current) {
      selfLevel.current = false;
      return;
    }
    setDayFocus(null);
    setStepDay(null);
    setStepSel(null);
  }, [level]);
  const toLevel = (l: OrbLevel) => {
    if (l !== level) selfLevel.current = true;
    p.onLevel(l);
  };
  const dayKey = dayFocus ?? todayKey;
  const drillDay = (key: string) => {
    setDayFocus(key);
    toLevel('T');
  };
  const drillStep = (item: OrbItem) => {
    setStepDay(item.date);
    setStepSel(item.key);
    toLevel('S');
  };
  const setErr = (m: string) => console.warn('[orb]', m);

  const go = (a: SwipeAction) => {
    if (a.kind === 'tool') {
      setKrnosView(a.view ?? null);
      p.onOpenTool(a.tool, a.opts);
    } else if (a.kind === 'session') {
      if (p.onOpenSession) p.onOpenSession(a.session);
      else p.onOpenTool(a.tool);
    } else if (a.level === 'T') {
      drillDay(a.dayKey ?? todayKey);
    } else if (a.level === 'S') {
      setStepDay(level === 'T' ? dayKey : stepDay);
      setStepSel(a.stepKey ?? null);
      toLevel('S');
    } else toLevel(a.level);
  };
  const acts = useOrbActions(p.userId, data, notes, p.onRefresh, { drillDay, drillStep, go, fail: setErr });

  // ---- the Step orb's task, its routine and runner --------------------------
  const stepDayKey = stepDay ?? todayKey;
  const pool = useMemo(() => buildPool(data.items, sector, p.steps, stepDayKey), [data.items, sector, p.steps, stepDayKey]);
  const step = useMemo(() => pool.find((i) => i.key === stepSel) ?? defaultStep(pool, p.steps, p.now), [pool, stepSel, p.steps, p.now]);
  const protocol = useMemo(() => (step ? matchProtocol(data.protocols, step) : null), [data.protocols, step]);
  const [routine, setRoutine] = useState<RoutineStep[] | null>(null);
  const protoId = protocol?.id ?? null;
  useEffect(() => {
    if (!protoId || !p.userId) {
      setRoutine(null);
      return;
    }
    let live = true;
    supabase
      .from('protocol_items')
      .select('id,title,duration_minutes,order')
      .eq('user_id', p.userId)
      .eq('protocol_id', protoId)
      .order('order', { ascending: true })
      .then(({ data: rows }) => {
        if (!live) return;
        setRoutine(((rows ?? []) as { id: string; title: string; duration_minutes: number | null }[]).map((r) => ({ id: r.id, title: r.title, minutes: r.duration_minutes && r.duration_minutes > 0 ? r.duration_minutes : 1 })));
      });
    return () => {
      live = false;
    };
  }, [protoId, p.userId]);
  const runner = useRunner(p.userId, step?.key ?? '', routine, protocol, step?.minutes ?? 30);

  // ---- ORDER widgets: existing tool state → adapters → one shell (orb/widgets/).
  const wctx: WidgetCtx = {
    userId: p.userId,
    now: p.now,
    sector,
    sectorName,
    level,
    spine: p.spine,
    projects: p.projects,
    today: p.today,
    data,
    steps: p.steps,
    step: step ?? null,
    runner,
    protocol,
    routine,
    acts,
    tick: data.tick,
    openTool: (id, opts) => {
      setKrnosView(null);
      p.onOpenTool(id, opts);
    },
  };
  const widgets = useOrbWidgets(wctx);
  const [pins, togglePin] = usePins();
  const onAgent = p.onAgent ? (seatId: string) => p.onAgent!(seatId) : undefined;

  const ctx: RowCtx = { userId: p.userId, sector, sectorName, spine: p.spine, data, today: p.today, now: p.now, notes, acts };
  const [oLocal, setOLocal] = useState<Record<string, OrientSentences>>({});
  const [dLocal, setDLocal] = useState<Record<string, string | null>>({});
  const [, porosTick] = useState(0);
  useEffect(() => subscribePoros(() => porosTick((n) => n + 1)), []);
  const loadingNow = data.loading && data.items.length === 0;
  let rows: RowSet;
  if (level === 'S') {
    if (step) {
      const project = step.designId ? (Object.values(p.projects).flat() as OracleProject[]).find((x) => x.id === step.designId) : undefined;
      const at = project && step.stageKey ? project.steps.findIndex((s) => s.id === step.stageKey) : -1;
      const node = sector && p.spine ? p.spine[sector] : null;
      rows = stepRows(ctx, {
        step,
        runner,
        protocol,
        routine,
        prior: project && at > 0 ? project.steps.slice(0, at).map((s) => ({ title: s.text, done: s.status === 'C' })) : [],
        subs: project && at >= 0 ? project.steps[at].substeps.filter((s) => !s.checked && s.text).map((s) => s.text) : [],
        serves: { today: todayObjective(ctx, stepDayKey), aim: aimsOf(node)[0]?.label ?? null, goal: goalName(sector, node), endeavour: node?.endeavour ?? null },
        onDone: () => void acts.done(step).then((ok) => ok && setStepSel(null)),
      });
    } else rows = emptyStepRows(ctx, loadingNow);
  } else if (level === 'T') rows = todayRows(ctx, dayKey);
  else rows = aimRows(ctx, level);
  // ORIENT = two sentences only (what am I doing and why · how will I know I'm done),
  // saved into ATLAS per sector + stage period.
  const oDay = level === 'T' ? dayKey : level === 'S' ? stepDayKey : todayKey;
  const oKey = orientKey(level, new Date(`${oDay}T12:00:00`));
  const oNode = sector && p.spine ? p.spine[sector] : null;
  const oStored = orientOf(oNode, oKey);
  const oLocalVal = sector ? oLocal[`${sector}|${oKey}`] : undefined;
  // Pre-filled from the ATLAS selections for this sector + the orb's level; the user's own text (even empty) wins.
  const oAtlas = atlasOf(oNode);
  const oPath = sector
    ? resolvePath(sector, {
        endeavour: oNode?.endeavour ?? null,
        goal: oAtlas.goal.text || null,
        aim: aimsOf(oNode)[0]?.label ?? null,
        today: currentOf(oAtlas.today)?.text ?? null,
        step: currentOf(oAtlas.step)?.text ?? null,
      })
    : null;
  const oPrefill = sector && oPath ? orientPrefill(sector, level, oPath) : { doing: '', done: '' };
  const oVal = oLocalVal ?? oStored ?? oPrefill;
  rows = {
    ...rows,
    orient: {
      ...rows.orient,
      question: ORIENT_Q.doing,
      line: oVal.doing || ORIENT_Q.doing,
      answer: [oVal.doing || ORIENT_Q.doing, oVal.done || ORIENT_Q.done],
      tone: oVal.doing ? undefined : 'quiet',
      widget: null,
    },
  };
  // REGULATE = POROS: one sentence from the resource table for this level's objective.
  const rObjective = oPath ? { E: oPath.endeavour, G: oPath.goal, A: oPath.aim, T: oPath.today, S: oPath.step }[level] ?? null : null;
  const rKey = rObjective ? porosKey(level, rObjective) : null;
  const rRec = sector && rKey ? cachedPoros(sector, rKey) ?? porosOf(oNode, rKey) : null;
  const rGenerated = readinessSentence(rRec);
  const rSaid = rRec?.said ?? rGenerated;
  const rNeedGen = needsSentence(rRec);
  const rNeed = rRec?.needSaid ?? rNeedGen;
  rows = {
    ...rows,
    regulate: {
      ...rows.regulate,
      question: REGULATE_Q.need,
      line: rSaid || rNeed || REGULATE_Q.need,
      answer: [rNeed || REGULATE_Q.need, rSaid || REGULATE_Q.have],
      tone: rSaid ? (readiness(rRec).missing.length ? 'warn' : undefined) : 'quiet',
      swipe: { kind: 'tool', tool: 'poros', label: 'POROS' },
      widget: null,
    },
  };
  // DIRECT = KYRS: "What should I do?" (the current priority) + "In what order?" (the sequence after it),
  // from the orb's KYRS-ordered open items for this sector at this level.
  const dPool = itemsForSector(data.items, sector).filter((i) => !i.completed);
  const dList =
    level === 'S'
      ? [...(step ? [step] : []), ...dPool.filter((i) => i.date === stepDayKey && i.key !== step?.key)]
      : level === 'T'
      ? dPool.filter((i) => i.date === dayKey)
      : dPool;
  const dTitles = dList.map((i) => i.title.trim().replace(/[.\s]+$/, '')).filter(Boolean);
  const dGenDo = dTitles[0] ? `${dTitles[0]} now.` : '';
  const dRest = dTitles.slice(1, 7);
  const dGenOrder = dRest.length ? `${dRest.join(' → ')}${dTitles.length > 7 ? ` (+${dTitles.length - 7} more)` : ''}.` : '';
  const dSaid = (part: 'do' | 'order') => {
    const k2 = `${oKey}|${part}`;
    const local = sector ? dLocal[`${sector}|${k2}`] : undefined;
    return local !== undefined ? local : atlasSentenceOf(oNode, 'direct', k2);
  };
  const dDo = dSaid('do') ?? dGenDo;
  const dOrder = dSaid('order') ?? dGenOrder;
  rows = {
    ...rows,
    direct: {
      ...rows.direct,
      question: DIRECT_Q.do,
      line: dDo || DIRECT_Q.do,
      answer: [dDo || DIRECT_Q.do, dOrder || DIRECT_Q.order],
      tone: dDo ? undefined : 'quiet',
      swipe: { kind: 'tool', tool: 'kyrs', label: 'KYRS' },
      widget: null,
    },
  };
  // EXECUTE = METIS: "What am I doing right now?" + "How do I carry it out?" (THEMIS routine,
  // TKNE sub-steps or a timer at Step level; progress through the work beneath at higher levels).
  const eSaid = (part: 'now' | 'how') => {
    const k2 = `${oKey}|${part}`;
    const local = sector ? dLocal[`x|${sector}|${k2}`] : undefined;
    return local !== undefined ? local : atlasSentenceOf(oNode, 'execute', k2);
  };
  const clean = (x: string) => x.trim().replace(/[.\s]+$/, '');
  let eGenNow = '';
  let eGenHow = '';
  if (level === 'S' && step) {
    const routineSteps = routine ?? [];
    const at = Math.min(runner.idx, Math.max(0, routineSteps.length - 1));
    const curTitle = routineSteps.length ? runner.cur?.title ?? step.title : step.title;
    eGenNow = step.completed ? `I've finished ${clean(step.title)}.` : `${clean(curTitle)} — ${runner.finished ? 'time is up' : `${mmss(runner.left)} left`}.`;
    const project = step.designId ? (Object.values(p.projects).flat() as OracleProject[]).find((x) => x.id === step.designId) : undefined;
    const pAt = project && step.stageKey ? project.steps.findIndex((x) => x.id === step.stageKey) : -1;
    const subs = project && pAt >= 0 ? project.steps[pAt].substeps.filter((x) => !x.checked && x.text).map((x) => x.text) : [];
    eGenHow = routineSteps.length
      ? `Follow ${protocol?.title ? clean(protocol.title) : 'the routine'}: ${routineSteps.slice(at, at + 5).map((x) => clean(x.title)).join(' → ')}.`
      : subs.length
      ? `Work through ${subs.slice(0, 5).map(clean).join(' → ')}.`
      : `Start a ${step.minutes}-minute timer and work until it rings.`;
  } else if (level !== 'S') {
    const pool = itemsForSector(data.items, sector).filter((i) => (level === 'T' ? i.date === dayKey : true));
    const open = pool.filter((i) => !i.completed);
    const done = pool.length - open.length;
    if (open[0]) eGenNow = `${clean(open[0].title)}.`;
    if (pool.length) eGenHow = `Work the ${level === 'T' ? "day's" : "week's"} list in order — ${done} of ${pool.length} done so far.`;
  }
  const eNow = eSaid('now') ?? eGenNow;
  const eHow = eSaid('how') ?? eGenHow;
  rows = {
    ...rows,
    execute: {
      ...rows.execute,
      question: EXECUTE_Q.now,
      line: eNow || EXECUTE_Q.now,
      answer: [eNow || EXECUTE_Q.now, eHow || EXECUTE_Q.how],
      tone: eNow ? undefined : 'quiet',
      widget: null,
    },
  };
  // REVISE = CLIO: four outcome buttons (complete / partial / incomplete / reschedule)
  // + an optional note. Both live in atlas.revise under this stage period.
  const vSaid = (part: 'outcome' | 'note') => {
    const k2 = `${oKey}|${part}`;
    const local = sector ? dLocal[`v|${sector}|${k2}`] : undefined;
    return local !== undefined ? local : atlasSentenceOf(oNode, 'revise', k2);
  };
  let vGenOutcome: ReviseOutcomeId | null = null;
  const vGen: string[] = [];
  if (level === 'S' && step) {
    if (step.completed) vGenOutcome = 'complete';
    else if (step.missed) {
      vGenOutcome = 'incomplete';
      vGen.push(`I didn't finish ${clean(step.title)} — ${clean(step.missed)}.`, `Deal with ${clean(step.missed)} before I try it again.`);
    }
  } else if (level !== 'S') {
    const pool = itemsForSector(data.items, sector).filter((i) => (level === 'T' ? i.date === dayKey : true));
    const done = pool.filter((i) => i.completed).length;
    if (pool.length) vGen.push(`I finished ${done} of ${pool.length} planned ${level === 'T' ? (dayKey === todayKey ? 'items so far today' : 'items that day') : 'items this week'}.`);
    const lesson = notes.get(level === 'T' ? `L:day:${dayKey}` : `L:week:${data.weekStartKey}`)?.text;
    if (lesson) vGen.push(`${clean(lesson)}.`);
  }
  const vOutRaw = vSaid('outcome');
  const vOutcome: ReviseOutcomeId | null = vOutRaw !== null ? (REVISE_OUTCOMES.some((o) => o.id === vOutRaw) ? (vOutRaw as ReviseOutcomeId) : null) : vGenOutcome;
  const vNote = vSaid('note') ?? vGen.join(' ');
  const vLabel = REVISE_OUTCOMES.find((o) => o.id === vOutcome)?.label;
  rows = {
    ...rows,
    revise: {
      ...rows.revise,
      question: REVISE_NOTE_Q,
      line: vLabel ?? REVISE_NOTE_Q,
      answer: [vLabel ?? REVISE_NOTE_Q],
      tone: vOutcome ? undefined : 'quiet',
      swipe: { kind: 'tool', tool: 'clio', label: 'CLIO' },
      widget: null,
    },
  };
  if (loadingNow && level !== 'S') {
    const w = { ...rows };
    for (const gg of GEAR_ORDER) w[gg] = { ...rows[gg], line: 'Finding your place…', answer: ['Finding your place…'], tone: 'quiet' };
    rows = w;
  }

  useEffect(() => {
    setPorosTarget(sector && rObjective ? { sector, stage: level, objective: rObjective } : null);
  }, [sector, level, rObjective]);

  // ▶ and the canvas swipe go to the selected row's target; a right-swipe on an open row does too.
  const sw = rows[g].swipe;
  const swKey = `${sw.tool}|${sw.kind === 'tool' ? `${JSON.stringify(sw.opts ?? null)}|${sw.view ?? ''}` : sw.kind}`;
  useEffect(() => {
    p.onTarget({ tool: sw.tool, opts: sw.kind === 'tool' ? sw.opts : undefined });
    setKrnosView(sw.kind === 'tool' ? sw.view ?? null : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swKey]);
  const swipeFn = useRef<() => void>(() => {});
  swipeFn.current = () => go(sw);
  p.swipeRef.current = () => swipeFn.current();
  const swipePan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, gs) => gs.dx > 16 && gs.dx > Math.abs(gs.dy) * 2,
      onPanResponderRelease: (_e, gs) => {
        if (gs.dx > 60) swipeFn.current();
      },
    }),
  ).current;

  // Quick crossfade when the selected letter or the orb changes.
  const fade = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    fade.setValue(0);
    Animated.timing(fade, { toValue: 1, duration: 200, useNativeDriver: true }).start();
  }, [g, level, fade]);

  // ---- the crescent: a lune. Outer edge = the orb's own circle (centre c, radius R);
  // inner edge = the same circle shifted right by d. Five horizontal slices.
  const c = R;
  const d = 0.16 * R;
  // FOCUS (lune + one letter's content) or ROWS (five collapsible lines).
  const [view, setViewState] = useState<'focus' | 'rows'>('focus');
  useEffect(() => {
    AsyncStorage.getItem('olmps.orb.view')
      .then((v) => v === 'rows' && setViewState('rows'))
      .catch(() => {});
  }, []);
  const setView = (v: 'focus' | 'rows') => {
    setViewState(v);
    AsyncStorage.setItem('olmps.orb.view', v).catch(() => {});
  };
  // EVEN (Rows only, the white dot at the top): five equal bands, each showing its
  // whole answer — read the orb top to bottom without opening anything.
  const [even, setEvenState] = useState(false);
  useEffect(() => {
    AsyncStorage.getItem('olmps.orb.even')
      .then((v) => v === '1' && setEvenState(true))
      .catch(() => {});
  }, []);
  const setEven = (v: boolean) => {
    setEvenState(v);
    AsyncStorage.setItem('olmps.orb.even', v ? '1' : '0').catch(() => {});
  };
  // Band boundaries (y of the 6 edges). Focus: five equal slices. Rows: the
  // expanded band grows, the other four compress to thin strips; the crescent
  // slices and the divider lines read the same animated boundaries.
  const stripH = 0.1 * (2 * R);
  const boundsFor = (ex: GearId | null): number[] => {
    const eq = (2 * R) / 5;
    const hs = GEAR_ORDER.map((gg) => (ex == null ? eq : gg === ex ? 2 * R - 4 * stripH : stripH));
    const out = [c - R];
    hs.forEach((h) => out.push(out[out.length - 1] + h));
    return out;
  };
  const [expanded, setExpanded] = useState<GearId | null>(p.selectedGear);
  useEffect(() => setExpanded(p.selectedGear), [p.selectedGear]);
  const target = view === 'rows' && !even ? boundsFor(expanded) : boundsFor(null);
  const fromRef = useRef<number[]>(target);
  const curRef = useRef<number[]>(target);
  const prog = useRef(new Animated.Value(1)).current;
  const [, force] = useState(0);
  const tKey = target.join(',');
  useEffect(() => {
    fromRef.current = curRef.current;
    prog.setValue(0);
    Animated.timing(prog, { toValue: 1, duration: 250, useNativeDriver: false }).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tKey]);
  useEffect(() => {
    const id = prog.addListener(({ value }) => {
      curRef.current = fromRef.current.map((f, i) => f + (target[i] - f) * value);
      force((n) => n + 1);
    });
    return () => prog.removeListener(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tKey]);
  const bounds = curRef.current.length === 6 ? curRef.current : target;
  const y0 = (i: number) => bounds[i];
  const hAt = (i: number) => bounds[i + 1] - bounds[i];
  const chord = (y: number) => Math.sqrt(Math.max(0, R * R - (y - c) * (y - c)));
  const hh = Math.sqrt(R * R - (d * d) / 4);
  const lune = `M${c + d / 2},${c - hh} A${R},${R} 0 1 0 ${c + d / 2},${c + hh} A${R},${R} 0 0 1 ${c + d / 2},${c - hh} Z`;


  const lineColor = (tone: RowSet[GearId]['tone'], sel: boolean) => (sel ? t.goldBright : tone === 'warn' ? t.behind : tone === 'quiet' ? t.ivoryFaint : t.ivory);
  const commitDirect = (part: 'do' | 'order', v: string | null) => {
    if (!sector || !p.userId) return;
    const k2 = `${oKey}|${part}`;
    setDLocal((m) => ({ ...m, [`${sector}|${k2}`]: v }));
    saveAtlasSentence(supabase, p.userId, sector, 'direct', k2, v).catch(() => {});
  };
  const commitRegulate = (field: 'said' | 'needSaid', v: string | null) => {
    if (!sector || !p.userId || !rKey || !rObjective) return;
    const base = rRec ?? emptyPoros(rObjective);
    const next = { ...base, [field]: v ?? undefined };
    cachePoros(sector, rKey, next);
    savePoros(supabase, p.userId, sector, rKey, next).catch(() => {});
  };
  const commitRevise = (part: 'outcome' | 'note', v: string | null) => {
    if (!sector || !p.userId) return;
    const k2 = `${oKey}|${part}`;
    setDLocal((m) => ({ ...m, [`v|${sector}|${k2}`]: v }));
    saveAtlasSentence(supabase, p.userId, sector, 'revise', k2, v).catch(() => {});
  };
  const pickOutcome = (v: ReviseOutcomeId | null) => {
    commitRevise('outcome', v ?? '');
    // On a Step, ✓ also ticks the task off (and un-ticks it when ✓ is taken back).
    if (level === 'S' && step) {
      if (v === 'complete' && !step.completed) void acts.done(step, true);
      else if (v !== 'complete' && vOutcome === 'complete' && step.completed) void acts.done(step, false);
    }
  };
  const reviseBody = (compact: boolean, size?: number) => (
    <ReviseOutcome
      value={vOutcome}
      onPick={pickOutcome}
      compact={compact}
      size={size}
      note={vNote}
      onNote={(v) => commitRevise('note', v === vGen.join(' ') ? null : v)}
      onOpen={() => go({ kind: 'tool', tool: 'clio', label: 'CLIO' })}
    />
  );
  const commitExecute = (part: 'now' | 'how', v: string | null) => {
    if (!sector || !p.userId) return;
    const k2 = `${oKey}|${part}`;
    setDLocal((m) => ({ ...m, [`x|${sector}|${k2}`]: v }));
    saveAtlasSentence(supabase, p.userId, sector, 'execute', k2, v).catch(() => {});
  };
  const rowBody = (gg: GearId) =>
    gg === 'revise' ? (
      reviseBody(false)
    ) : gg === 'execute' ? (
      <View style={{ alignSelf: 'stretch', gap: 8 * k }}>
        <SentenceField question={EXECUTE_Q.now} said={eSaid('now')} generated={eGenNow} onCommit={(v) => commitExecute('now', v)} />
        <SentenceField question={EXECUTE_Q.how} said={eSaid('how')} generated={eGenHow} onCommit={(v) => commitExecute('how', v)} />
      </View>
    ) : gg === 'direct' ? (
      <View style={{ alignSelf: 'stretch', gap: 8 * k }}>
        <SentenceField question={DIRECT_Q.do} said={dSaid('do')} generated={dGenDo} onCommit={(v) => commitDirect('do', v)} />
        <SentenceField question={DIRECT_Q.order} said={dSaid('order')} generated={dGenOrder} onCommit={(v) => commitDirect('order', v)} />
      </View>
    ) : gg === 'regulate' ? (
      <View style={{ alignSelf: 'stretch', gap: 8 * k }}>
        <SentenceField question={REGULATE_Q.need} said={rRec?.needSaid ?? null} generated={rNeedGen} disabledHint={rObjective ? null : 'Set an objective in ATLAS first.'} onCommit={(v) => commitRegulate('needSaid', v)} />
        <SentenceField question={REGULATE_Q.have} said={rRec?.said ?? null} generated={rGenerated} disabledHint={rObjective ? null : ''} onCommit={(v) => commitRegulate('said', v)} />
      </View>
    ) : gg === 'orient' ? (
      <OrientFields
        userId={p.userId}
        sector={sector}
        orientKey={oKey}
        value={oVal}
        onChange={(v) => sector && setOLocal((m) => ({ ...m, [`${sector}|${oKey}`]: v }))}
      />
    ) : null;

  // Content box: inset from the crescent, centred, inside the circle's chord.
  const boxW = Math.round(S * (g === 'orient' || g === 'regulate' || g === 'direct' || g === 'execute' || g === 'revise' ? 0.8 : 0.66));
  const boxL = Math.round(c + d / 2 - boxW / 2 + S * 0.01);
  const boxT = Math.round(S * 0.22);
  const boxH = Math.round(S * 0.58);

  return (
    <OrbScale.Provider value={k}>
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      {/* Circular safety clip: nothing in here can ever render outside the orb. */}
      <View style={{ position: 'absolute', left: 0, top: 0, width: 2 * R, height: 2 * R, borderRadius: R, overflow: 'hidden' }} pointerEvents="box-none">
      {/* No orb body fill: the sky (night or day) shows through, so text reads in both themes. */}
      {/* The lune: clipped slices, horizontal dividers, the selected slice filled gold. */}
      <Svg width={2 * R} height={2 * R} style={{ position: 'absolute', left: 0, top: 0 }} pointerEvents="none">
        <Defs>
          <ClipPath id="orbc">
            <Circle cx={c} cy={c} r={R} />
          </ClipPath>
          <ClipPath id="lune">
            <Path d={lune} />
          </ClipPath>
          <ClipPath id="selband">
            <Rect x={0} y={y0(GEAR_ORDER.indexOf(g))} width={2 * R} height={Math.max(1, hAt(GEAR_ORDER.indexOf(g)))} />
          </ClipPath>
        </Defs>
        {/* Unselected slices are unfilled — hairlines only. */}
        <G clipPath="url(#lune)">
          {GEAR_ORDER.map((gg, i) =>
            gg === g ? (
              // The selected slice is white veined marble (the mockup's lit crescent).
              <G key={gg} clipPath="url(#selband)">
                <SvgImage href={ORB_MARBLE_LIGHT} x={0} y={y0(i)} width={2 * R} height={Math.max(1, hAt(i))} preserveAspectRatio="xMidYMid slice" />
              </G>
            ) : null,
          )}
          {[1, 2, 3, 4].map((i) => (
            <Line key={i} x1={0} x2={2 * R} y1={y0(i)} y2={y0(i)} stroke={t.gold} strokeOpacity={0.75} strokeWidth={1} />
          ))}
        </G>
        {view === 'rows' ? (
          <G clipPath="url(#orbc)">
            {[1, 2, 3, 4].map((i) => (
              <Line key={i} x1={0} x2={2 * R} y1={y0(i)} y2={y0(i)} stroke={t.gold} strokeOpacity={0.6} strokeWidth={1} />
            ))}
          </G>
        ) : null}
        <Path d={lune} fill="none" stroke={t.gold} strokeOpacity={0.8} strokeWidth={1} />
      </Svg>
      {GEAR_ORDER.map((gg, i) => {
        // Each letter sits where its band is closest to the orb's centre (the crescent
        // is widest there), with a small gap inside the rim — never clipped by the edge.
        const fs = Math.max(12, d * 0.62);
        const lo = y0(i) + fs * 0.65;
        const hi = y0(i + 1) - fs * 0.65;
        const ym = lo <= hi ? Math.min(hi, Math.max(lo, c)) : y0(i) + hAt(i) / 2;
        // The glyph's outer corner (top-left above the centre line, bottom-left below)
        // must clear the rim by RIM_GAP — near the caps the curve, not the side, is what clips.
        const hh = fs * 0.5;
        const hw = fs * 0.38;
        const corner = ym < c ? ym - hh : ym + hh;
        const xm = Math.max(c - chord(ym) + d / 2, c - chord(corner) + hw + RIM_GAP);
        const sel = gg === g;
        return (
          <Pressable
            key={gg}
            onPress={() => {
              if (view === 'rows') {
                setEven(false);
                setExpanded(gg);
              }
              p.onPickRow(gg);
            }}
            accessibilityLabel={GEAR_LABEL[gg].name}
            style={[{ position: 'absolute', left: xm - 16, top: y0(i), width: 32, height: hAt(i) }, { outlineStyle: 'none' } as object]}
          >
            <Text style={[styles.letterText, { position: 'absolute', left: 0, right: 0, textAlign: 'center', top: ym - y0(i) - fs * 0.62, fontSize: fs, lineHeight: fs * 1.24 }, sel && { color: '#2a1d08' }]}>{GEAR_LABEL[gg].letter}</Text>
          </Pressable>
        );
      })}


      {view === 'focus' ? (
        /* The selected letter's widget fills the orb body (its question small above). */
        <Animated.View style={[styles.body, { left: boxL, top: boxT, width: boxW, height: boxH, opacity: fade }]} {...swipePan.panHandlers}>
          {rowBody(g)}
        </Animated.View>
      ) : (
        /* ROWS: the lune's dividers continue across the orb; collapsed band = the line, open band = the widget. */
        <>
          {GEAR_ORDER.map((gg, i) => {
            const ym = y0(i) + hAt(i) / 2;
            const isExp = !even && gg === expanded && hAt(i) > stripH * 2;
            const fs0 = 13.5 * k;
            // The box must sit inside the circle's chord at its narrowest (farthest-from-centre) y.
            const top = isExp ? Math.max(y0(i) + 6, c - 0.78 * R) : even ? Math.max(y0(i) + 4, c - 0.86 * R) : ym - fs0;
            const bottom = isExp ? Math.min(y0(i + 1) - 6, c + 0.78 * R) : even ? Math.min(y0(i + 1) - 4, c + 0.86 * R) : ym + fs0;
            // ORIENT's two text fields use the band's middle chord so they run as wide as the orb allows.
            const yfar = isExp && (gg === 'orient' || gg === 'regulate' || gg === 'direct' || gg === 'execute' || gg === 'revise') ? Math.abs((top + bottom) / 2 - c) + 34 * k : Math.max(Math.abs(top - c), Math.abs(bottom - c));
            const ch = chord(c + Math.min(R, yfar));
            const left = c + d - ch + 10;
            const w = Math.max(30, c + ch - 10 - left);
            const sel = gg === g;
            const fs = Math.max(8, Math.min(fs0, w / 13));
            if (isExp) {
              // The band is narrower than the Focus box near the rim: shrink the widget to fit.
              const ks = gg === 'orient' || gg === 'regulate' || gg === 'direct' || gg === 'execute' || gg === 'revise' ? k : k * Math.min(1, w / boxW);
              return (
                <View key={gg} {...swipePan.panHandlers} style={{ position: 'absolute', left, top, width: w, height: Math.max(0, bottom - top), justifyContent: 'center', overflow: 'hidden' }}>
                  <OrbScale.Provider value={ks}>{rowBody(gg)}</OrbScale.Provider>
                </View>
              );
            }
            const open = () => {
              tourBus.emit('orb:expand');
              setEven(false);
              setExpanded(gg);
              p.onPickRow(gg);
            };
            if (gg === 'revise') {
              // REVISE's band is just its four outcome buttons; tapping the band (not a button) opens it.
              const bh = even ? Math.max(0, bottom - top) : hAt(i);
              return (
                <Pressable key={gg} ref={i === 0 ? orbRow.onLayoutRef : undefined} onPress={open} style={[{ position: 'absolute', left: 0, top: y0(i), width: 2 * R, height: hAt(i), paddingLeft: left, paddingRight: Math.max(0, 2 * R - left - w), justifyContent: 'center' }, { outlineStyle: 'none' } as object]}>
                  {reviseBody(true, Math.max(14, Math.min(44, bh * 0.62, w / 7)))}
                </Pressable>
              );
            }
            if (even) {
              // The whole answer, wrapped and sized to fit its band.
              const fit = fitAnswer(rows[gg].answer, w, Math.max(0, bottom - top), fs0);
              return (
                <Pressable key={gg} ref={i === 0 ? orbRow.onLayoutRef : undefined} onPress={open} style={[{ position: 'absolute', left: 0, top: y0(i), width: 2 * R, height: hAt(i), paddingLeft: left, paddingRight: Math.max(0, 2 * R - left - w), paddingTop: top - y0(i), paddingBottom: Math.max(0, y0(i + 1) - bottom), justifyContent: 'center' }, { outlineStyle: 'none' } as object]}>
                  <Text style={[styles.bandText, { fontSize: fit.fs, lineHeight: fit.fs * 1.2, textAlign: 'center', color: sel ? t.goldBright : t.ivorySoft }]} numberOfLines={fit.lines}>
                    {rows[gg].answer.map((a, j) => (
                      <Text key={j} style={j === 0 ? { color: lineColor(rows[gg].tone, sel), fontWeight: '700' } : undefined}>
                        {j ? '  ·  ' : ''}
                        {a}
                      </Text>
                    ))}
                  </Text>
                </Pressable>
              );
            }
            return (
              <Pressable
                key={gg}
                ref={i === 0 ? orbRow.onLayoutRef : undefined}
                onPress={open}
                style={[{ position: 'absolute', left: 0, top: y0(i), width: 2 * R, height: hAt(i), paddingLeft: left, paddingRight: Math.max(0, 2 * R - left - w), flexDirection: 'row', alignItems: 'center', gap: 6 }, { outlineStyle: 'none' } as object]}
              >
                <Text style={[styles.bandText, { fontSize: fs, flex: 1, color: lineColor(rows[gg].tone, sel) }]} numberOfLines={1}>
                  {rows[gg].line}
                </Text>
              </Pressable>
            );
          })}
        </>
      )}
      </View>
      {/* The glowing gold rim (with its halo and flares), over the edge. */}
      <Image
        source={ORB_RIM}
        style={{ position: 'absolute', left: R - R / ORB_RIM_FRAC, top: R - R / ORB_RIM_FRAC, width: (2 * R) / ORB_RIM_FRAC, height: (2 * R) / ORB_RIM_FRAC, ...NO_TOUCH }}
        resizeMode="stretch"
      />
      {/* Rows only: the white dot on the rim's crown evens the five bands (tap again to undo). */}
      {view === 'rows' ? (
        <Pressable
          onPress={() => setEven(!even)}
          hitSlop={10}
          accessibilityLabel={even ? 'Open one row' : 'Show every answer'}
          style={[styles.evenDot, { left: R - 8, top: -8 }, even && { boxShadow: `0 0 10px ${t.goldBright}`, borderColor: t.goldBright }, { outlineStyle: 'none' } as object]}
        />
      ) : null}
      {/* View toggle: ◐ Focus / ☰ Rows — outside the orb, just below its rim. */}
      <View style={[styles.toggle, { left: R - 34, top: 2 * R + 20 }]}>
        {(['focus', 'rows'] as const).map((v) => (
          <Pressable key={v} onPress={() => setView(v)} accessibilityLabel={v === 'focus' ? 'Focus view' : 'Rows view'} hitSlop={6} style={[styles.toggleBtn, view === v && { backgroundColor: t.gold }, { outlineStyle: 'none' } as object]}>
            <Text style={[styles.toggleText, view === v && { color: t.onGold }]}>{v === 'focus' ? '◐' : '☰'}</Text>
          </Pressable>
        ))}
      </View>
      {/* "?" explainer: beside the Focus / Rows switch, just below the orb's rim. */}
      <ScreenHelp
        help={orbHelp({ complete: REVISE_OUTCOMES[0].color, partial: REVISE_OUTCOMES[1].color, incomplete: REVISE_OUTCOMES[2].color, reschedule: REVISE_OUTCOMES[3].color })}
        style={{ position: 'absolute', left: R + 34 + 12, top: 2 * R + 21 }}
      />
    </View>
    </OrbScale.Provider>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    toggle: { position: 'absolute', width: 68, height: 28, flexDirection: 'row', borderRadius: 14, borderWidth: 1.5, borderColor: t.gold, overflow: 'hidden', backgroundColor: '#0b1226', boxShadow: `0 0 10px ${t.goldGlow}` } as object,
    toggleBtn: { flex: 1, alignItems: 'center', justifyContent: 'center' },
    toggleText: { color: t.goldBright, fontSize: 15, fontWeight: '800' },
    bandText: { color: t.ivory, fontFamily: DISPLAY_FONT },
    evenDot: { position: 'absolute', width: 16, height: 16, borderRadius: 8, backgroundColor: '#ffffff', borderWidth: 2, borderColor: 'rgba(255,255,255,0.6)', boxShadow: '0 0 6px rgba(255,255,255,0.8)' } as object,
    body: { position: 'absolute', alignItems: 'stretch', justifyContent: 'center', overflow: 'hidden' },
    question: { color: '#e9e2cf', fontSize: 10.5, fontStyle: 'italic', fontFamily: DISPLAY_FONT, textAlign: 'center', alignSelf: 'stretch' },
    letterText: { fontFamily: DISPLAY_FONT, color: t.goldBright },
  }),
);
